import React from "react";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import FormatColorFillOutlinedIcon from "@mui/icons-material/FormatColorFillOutlined";
import FormatColorResetOutlinedIcon from "@mui/icons-material/FormatColorResetOutlined";
import FormatItalicOutlinedIcon from "@mui/icons-material/FormatItalicOutlined";
import TextFieldsOutlinedIcon from "@mui/icons-material/TextFieldsOutlined";
import {
  CANVAS_FONT_FAMILIES,
  CANVAS_FONT_WEIGHTS,
  ITALIC_FONT_STYLE,
} from "../../../features/canvas/canvasFonts";
import {
  DEFAULT_SHAPE_FILL,
  TRANSPARENT_CANVAS_FILL,
} from "../../../features/canvas/nativeCanvasElements";
import {
  ActionButton,
  StyleColorInput,
  StyleNumberInput,
  StyleSelect,
  ToggleButton,
  createStyleChange,
} from "./styleBarControls";

const STYLE_BAR_CLASS =
  "pointer-events-auto absolute bottom-4 left-1/2 z-30 flex max-w-[calc(100%-24px)] -translate-x-1/2 items-center gap-2 overflow-x-auto rounded-xl border border-border bg-surface/95 px-3 py-2 shadow-2xl backdrop-blur";
const NO_FILL_LABEL = "No fill";

const FONT_FAMILY_OPTIONS = CANVAS_FONT_FAMILIES.map((family) => ({
  optionLabel: family.label,
  optionValue: family.id,
}));
const FONT_WEIGHT_OPTIONS = CANVAS_FONT_WEIGHTS.map((weight) => ({
  optionLabel: weight.label,
  optionValue: weight.value,
}));

/**
 * @param {object} props
 * @returns {JSX.Element | null}
 */
const NativeCanvasStyleBar = ({
  onDelete,
  onDuplicate,
  onStyleChange,
  selectedCount,
  style,
}) => {
  if (!selectedCount) return null;
  const isItalic = style.fontStyle === ITALIC_FONT_STYLE;
  const isFillTransparent = style.fill === TRANSPARENT_CANVAS_FILL;
  // Native color inputs cannot hold "transparent"; while no-fill is active
  // the swatch shows the fill a re-toggle would restore.
  const fillInputValue = isFillTransparent ? DEFAULT_SHAPE_FILL : style.fill;

  return (
    <div aria-label="Canvas selection styles" className={STYLE_BAR_CLASS}>
      <StyleColorInput
        Icon={BorderColorOutlinedIcon}
        label="Stroke color"
        name="stroke"
        onStyleChange={onStyleChange}
        value={style.stroke}
      />
      <StyleColorInput
        Icon={FormatColorFillOutlinedIcon}
        label="Fill color"
        name="fill"
        onStyleChange={onStyleChange}
        value={fillInputValue}
      />
      <ToggleButton
        Icon={FormatColorResetOutlinedIcon}
        isPressed={isFillTransparent}
        label={NO_FILL_LABEL}
        onClick={() =>
          createStyleChange({
            key: "fill",
            onStyleChange,
            value: isFillTransparent ? DEFAULT_SHAPE_FILL : TRANSPARENT_CANVAS_FILL,
          })
        }
      />
      <StyleNumberInput
        Icon={BorderColorOutlinedIcon}
        label="Stroke width"
        name="strokeWidth"
        onStyleChange={onStyleChange}
        value={style.strokeWidth}
      />
      <StyleNumberInput
        Icon={TextFieldsOutlinedIcon}
        label="Text size"
        name="fontSize"
        onStyleChange={onStyleChange}
        value={style.fontSize}
      />
      <StyleSelect
        label="Font family"
        name="fontFamily"
        onChange={(value) =>
          createStyleChange({ key: "fontFamily", onStyleChange, value })
        }
        options={FONT_FAMILY_OPTIONS}
        value={style.fontFamily}
      />
      <StyleSelect
        label="Font weight"
        name="fontWeight"
        onChange={(value) =>
          createStyleChange({ key: "fontWeight", onStyleChange, value: Number(value) })
        }
        options={FONT_WEIGHT_OPTIONS}
        value={style.fontWeight}
      />
      <ToggleButton
        Icon={FormatItalicOutlinedIcon}
        isPressed={isItalic}
        label="Italic"
        onClick={() =>
          createStyleChange({
            key: "fontStyle",
            onStyleChange,
            value: isItalic ? "normal" : ITALIC_FONT_STYLE,
          })
        }
      />
      <ActionButton
        Icon={ContentCopyOutlinedIcon}
        label="Duplicate selection"
        onClick={onDuplicate}
      />
      <ActionButton
        Icon={DeleteOutlineOutlinedIcon}
        label="Delete selection"
        onClick={onDelete}
      />
    </div>
  );
};

export default NativeCanvasStyleBar;
